/**
 * What a screen shows where a linked row is gone. Deleting an agent keeps its
 * calls, campaigns, phone numbers and knowledge bases (migrations
 * 20260926120000 / 20260926130000) and deleting a phone number keeps its
 * campaigns (20260925140000): every link to the row turns NULL, and every place
 * that showed it shows N/A instead.
 */
export const NA = "N/A";

/** Shown only while a real read is pending, naming what it reads. */
export const LOADING = { agent: "Loading the agent…", number: "Loading the number…" } as const;

/**
 * `value`, or N/A — labelled ("Agent: N/A") where a bare N/A would not say what
 * it stands for: a subtitle joining two values, a filter chip.
 */
export const orNA = (value: string | null | undefined, label?: string): string =>
  value || (label ? `${label}: ${NA}` : NA);

/**
 * A linked row as a screen may use it: null when it is gone — no link, or its
 * read answered 404 (`gone`: a deleted agent) — and the screen treats it so;
 * otherwise the copy the read holds. A re-read that failed any other way keeps
 * that copy, so a passing error never turns a live agent into N/A; undefined
 * means there is no copy yet (pending, or a first read that failed).
 */
export const linkedRow = <T>(id: string | null | undefined, data: T | undefined, gone: boolean): T | null | undefined =>
  !id || gone ? null : data;

/**
 * A linked row's name: N/A at once when there is no link, `loading` while its
 * read is pending, then the name — or N/A when there is none to show.
 */
export const linkedName = (
  link: { id: string | null | undefined; name: string | null | undefined; pending: boolean },
  loading: string,
  label?: string,
): string => (link.id && link.pending ? loading : orNA(link.id ? link.name : null, label));
